// Plan 08-02 — silent IdP re-exchange with cross-tab coordination.
//
// NY-08-03 (concurrent silent refreshes coalesce to ONE network call):
//   1. Read $authBearer; null → return false (no session to refresh).
//   2. If exp_ms - Date.now() > REFRESH_LEAD_MS → return false (still fresh).
//   3. Acquire navigator.locks.request('runegambit.auth-refresh',
//      { mode: 'exclusive', ifAvailable: true }, ...). When the lock is
//      held by another tab/call, our callback fires with `null` and we
//      return false (Pitfall 8 — coalesce, don't fan out).
//   4. INSIDE the lock callback, RE-READ $authBearer.exp_ms. Another tab
//      may have refreshed while we were spinning up; if now valid, return.
//   5. Re-trigger the IdP that issued the prior session via the
//      idp_kind-keyed adapter (apple.silentReauthApple /
//      google.silentReauthGoogle). The adapter returns a fresh id_token.
//   6. POST /api/auth/exchange via exchangeIdpToken (single network call).
//   7. Broadcast on BroadcastChannel('runegambit-auth') so sibling tabs
//      drop their in-flight retries and re-read the atom.
//
// REFRESH_LEAD_MS = 50min: JWT TTL is 24h (Phase 2 SUMMARY), but we refresh
// proactively well before expiry so a long-running tab never sees a 401
// from the bearer's own clock. The atom listener in AuthProvider also
// triggers this on visibility-change so a backgrounded tab catches up.

import { $authBearer, clearAuth } from '../stores/auth.js'
import { exchangeIdpToken } from './exchange.js'
import {
  type AuthExchangeResult,
  AuthInvalidError,
  AuthLockedError,
  type IdpKind,
} from './types.js'

export const REFRESH_LEAD_MS = 50 * 60 * 1000 // 50min lead window
export const REFRESH_LOCK_NAME = 'runegambit.auth-refresh'
export const REFRESH_CHANNEL_NAME = 'runegambit-auth'

interface BroadcastMessage {
  kind: 'refreshed' | 'logout'
  user_uuid?: string
}

function broadcast(msg: BroadcastMessage): void {
  if (typeof BroadcastChannel === 'undefined') return
  try {
    const ch = new BroadcastChannel(REFRESH_CHANNEL_NAME)
    ch.postMessage(msg)
    ch.close()
  } catch {
    // BroadcastChannel can fail in some test envs / iframes — best-effort.
  }
}

function needsRefresh(): boolean {
  const bearer = $authBearer.get()
  if (!bearer) return false
  const now = Date.now()
  // ARCA-115: an ALREADY-EXPIRED bearer is a dead session, not a live one
  // worth proactively keeping alive. Driving an IdP silent re-auth for it on
  // mount surfaces a Google One-Tap / sign-in prompt (silentReauthGoogle →
  // accounts.id.prompt) to a returning visitor who may just want the no-signup
  // "Practice vs the bot" path — the holder is a GUEST now (AC #2). Skip it;
  // AuthProvider already renders `kind: 'guest'` for an expired bearer, and
  // apiFetch's 401 path (forceSilentRefresh, which bypasses this gate) still
  // owns mid-session recovery for a bearer that expires while actively in use.
  if (bearer.exp_ms <= now) return false
  return bearer.exp_ms - now <= REFRESH_LEAD_MS
}

async function getSilentIdToken(
  idp_kind: IdpKind,
  nonce: string,
): Promise<string> {
  if (idp_kind === 'apple') {
    const mod = await import('./apple.js')
    return mod.silentReauthApple({ nonce })
  }
  const mod = await import('./google.js')
  return mod.silentReauthGoogle({ nonce })
}

/**
 * Returns true if a refresh actually happened (we acquired the lock and
 * issued the network call). Returns false in every other case (no bearer,
 * still fresh, lock not granted, race-resolved before we hit the wire).
 *
 * Throws ONLY if the IdP adapter or exchange itself fails AND we owned the
 * lock when it failed. Callers (AuthProvider) treat throws as "route to
 * splash login UI".
 */
export async function silentRefreshIfNeeded(): Promise<boolean> {
  // Cheap pre-check before grabbing a lock — most calls hit this fast path.
  if (!needsRefresh()) return false

  const locks = navigator.locks
  if (!locks) {
    // No Web Locks support — fall back to single-flight in this tab. Better
    // than nothing for cross-tab coordination; production targets all ship
    // navigator.locks per https://caniuse.com/mdn-api_lockmanager.
    return runRefresh()
  }

  // ifAvailable: true → lock argument is `null` when another tab/call holds
  // the lock. We MUST return false in that case so the caller knows no
  // refresh ran.
  const ranRefresh = await locks.request(
    REFRESH_LOCK_NAME,
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (!lock) return false // another holder owns it; coalesce
      // Re-read inside the lock — sibling may have already refreshed.
      if (!needsRefresh()) return false
      return runRefresh()
    },
  )

  return ranRefresh as boolean
}

/**
 * Force an IdP-driven silent re-auth + /api/auth/exchange round-trip,
 * bypassing the `needsRefresh()` clock gate. Plan 14-07 (MUI-08) calls
 * this from `apiFetch` after a 401 — by that point the server has already
 * decided the bearer is invalid, so the lead-time gate is meaningless.
 *
 * Returns:
 *   - `true` on successful rotation (bearer atom updated via `saveAuth`).
 *   - `false` on every failure path: no bearer present, IdP refused, exchange
 *     returned non-200 (including 401/429/5xx — Open Question 4 lock), or a
 *     network error occurred. AuthLockedError (423) re-throws because the
 *     caller (AuthProvider) must surface the deletion-grace UI.
 *
 * NOTE: does NOT acquire `navigator.locks` — the 401 path is per-request and
 * already serialized by the apiFetch call site. If two apiFetch calls 401
 * concurrently they will both attempt a refresh; the second one's exchange
 * succeeds against the freshly-rotated atom (or its own IdP redeem races
 * benignly — the result is idempotent: the latest fresh bearer wins).
 */
export function forceSilentRefresh(): Promise<boolean> {
  return runRefresh()
}

async function runRefresh(): Promise<boolean> {
  const bearer = $authBearer.get()
  if (!bearer) return false
  const idp_kind = bearer.idp_kind

  // Plan 09-11 / CR-01: ONE nonce per refresh cycle, threaded through the
  // adapter (which embeds it in the IdP-issued JWT — Apple raw; Google
  // SHA-256 hashed inside google.ts) AND the exchange POST body. Backend's
  // jwks.ts knows the per-IdP transform and compares correctly. Pre-fix
  // this generated TWO randomUUIDs (one in the adapter, a separate one in
  // the body) → silent refresh always 401'd on the strict path.
  const nonce = crypto.randomUUID()

  let idToken: string
  try {
    idToken = await getSilentIdToken(idp_kind, nonce)
  } catch {
    // IdP refused to silently re-auth — caller routes to splash. We do NOT
    // clearAuth here because the existing bearer may still be usable until
    // its real expiry; the AuthProvider's 401 path handles full eviction.
    return false
  }

  let result: AuthExchangeResult
  try {
    result = await exchangeIdpToken({
      idp_kind,
      id_token: idToken,
      nonce, // CR-01: SAME nonce as the adapter received
    })
  } catch (err) {
    if (err instanceof AuthInvalidError) {
      // 401 already cleared the atom inside exchangeIdpToken.
      return false
    }
    if (err instanceof AuthLockedError) {
      // Account in the 7-day grace — let the AuthProvider surface the UI.
      // Re-throw so the caller knows.
      clearAuth()
      throw err
    }
    // 5xx / network error: leave the existing bearer in place, return false
    // so the caller can retry on next tick.
    return false
  }

  // CR-03 fix (Phase 14 fix-pass iter 1): `exchangeIdpToken` already
  // called `saveAuth(result)` on success at apps/web/src/auth/exchange.ts:96.
  // The pre-fix code re-called saveAuth here on the "idempotent (same
  // payload)" assumption, but that assumption depends on saveAuth being
  // a pure write with NO observable side effects — and saveAuth wraps
  // `$authBearer.set()` (a nanostores atom). A second set with byte-equal
  // payload still notifies every subscriber a second time. The atom has
  // no listeners that double-fire today, but a future
  // BroadcastChannel-on-atom-change wiring (or any react useStore
  // consumer that does work on each set) would silently double-fire on
  // the refresh path. Removing this redundant call closes the latent bug
  // and leaves saveAuth invoked exactly once per successful exchange.
  broadcast({ kind: 'refreshed', user_uuid: result.user_uuid })
  return true
}
